import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { LuTriangleAlert } from 'react-icons/lu';
import Button from './Button';

/**
 * ConfirmDialog – a React-based replacement for window.confirm().
 *
 * Props:
 *   isOpen       {boolean}  – controls visibility
 *   title        {string}   – dialog heading
 *   message      {string}   – body text
 *   confirmLabel {string}   – confirm button label  (default: 'Confirm')
 *   cancelLabel  {string}   – cancel button label   (default: 'Cancel')
 *   variant      {string}   – confirm button variant (default: 'danger')
 *   onConfirm    {Function} – called when the user confirms
 *   onCancel     {Function} – called when the user cancels / closes
 */
const ConfirmDialog = ({
  isOpen,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}) => {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const isDanger = variant === 'danger';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      <div className="relative w-full max-w-md animate-enter rounded-xl border border-ui-line bg-ui-card p-6 shadow-pop">
        <div className="flex items-start gap-4">
          {isDanger && (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300">
              <LuTriangleAlert aria-hidden className="size-5" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 id="confirm-dialog-title" className="text-base font-semibold text-ui-ink">
              {title}
            </h2>
            {message && <p className="mt-1.5 text-sm leading-relaxed text-ui-ink-2">{message}</p>}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={variant} onClick={onConfirm} autoFocus>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
